import {
    BaseFeatureArgs,
    FixedFilterGenerator,
    InitialQueryState,
    QueryManagerOptions,
    SkipOrArgs,
} from '@common/types/queryState';
import { isEmpty } from '@common/utils/typeGuards';
import { useClips, UseClipsArgs } from '@features/Clip';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import { useCollectionAssetIds } from './useCollectionAssetIds';
import { composeGenerators } from '@common/utils/query/composeGenerators';

export interface UseCollectionAssetsArgs extends BaseFeatureArgs {
    collectionId: string;
}

const collectionAssetsFilterGenerator: FixedFilterGenerator<
    BaseFeatureArgs & { assetIds: string[] }
> = (args) => {
    const uniqueIds = Array.from(new Set(args.assetIds));
    const formattedIds = uniqueIds.map((id) => `"${id}"`).join(' ');

    return [
        `{!tag=clipOrSubclipHasAttachedEditorialObjectId}(clipOrSubclipHasAttachedEditorialObjectId:(${formattedIds}) OR clipOrSubclipHasAttachedEditorialObjectParentId:(${formattedIds}))`,
        'funnel:(MediaObjectAnnotation ReviewComment ClipAnnotation TranscriptAnnotation Chapter) -render_originalMediaObjectId:[* TO *]',
    ];
};

export const useCollectionAssets = (
    initialArgs: InitialQueryState = {},
    args: SkipOrArgs<UseCollectionAssetsArgs>,
    options?: QueryManagerOptions,
) => {
    const eoArgs = useMemo(() => {
        if (args === skipToken) return skipToken;

        const {
            fixedFilterGenerators: _fixed,
            overrideFilterGenerator: _override,
            ...cleanArgs
        } = args;

        return cleanArgs as UseCollectionAssetsArgs;
    }, [args]);

    const { data: assetIdsResponse, isFetching: isAssetIdsFetching } =
        useCollectionAssetIds(eoArgs);

    const collectionId = args === skipToken ? undefined : args.collectionId;

    const assetIds: string[] = useMemo(() => {
        if (!collectionId) return [];

        const childIds = assetIdsResponse?.map((asset) => asset.id) || [];
        return [collectionId, ...childIds];
    }, [assetIdsResponse, collectionId]);

    const queryArgs: SkipOrArgs<UseClipsArgs> = useMemo(() => {
        if (args === skipToken || isAssetIdsFetching || isEmpty(assetIds)) {
            return skipToken;
        }

        const mergedGenerators = composeGenerators(
            collectionAssetsFilterGenerator,
            args.fixedFilterGenerators,
        );

        return {
            ...args,
            assetIds,
            fixedFilterGenerators: mergedGenerators,
        } as UseClipsArgs;
    }, [args, assetIds, isAssetIdsFetching]);

    const { isFetching: isAssetsFetching, ...queryResult } = useClips(
        initialArgs,
        queryArgs,
        options,
    );

    const isCurrentlyFetching = isAssetIdsFetching || isAssetsFetching;

    return {
        ...queryResult,
        isFetching: isCurrentlyFetching,
        isLoading: isCurrentlyFetching,
    };
};
